import {
  createUserWithEmailAndPassword,
  getIdTokenResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type User,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getFirebaseServices } from "./firebase";

export type UserRole = "customer" | "reseller" | "partner" | "sales_admin" | "finance_admin" | "warehouse" | "owner";

export interface Session {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
  emailVerified: boolean;
  providerIds: string[];
  organizationId?: string;
}

function actionCodeSettings() {
  return {
    url: import.meta.env.VITE_AUTH_ACTION_URL || window.location.origin,
    handleCodeInApp: false,
  };
}

export const adminRoles: UserRole[] = ["sales_admin", "finance_admin", "warehouse", "owner"];
export const masterAdminEmail = "alabiankebab@gmail.com";

async function sessionFromUser(user: User, forceRefresh = false): Promise<Session> {
  const token = await getIdTokenResult(user, forceRefresh);
  const claimRole = typeof token.claims.role === "string" ? token.claims.role : "customer";
  const isMasterAdmin = user.emailVerified && user.email?.trim().toLowerCase() === masterAdminEmail;
  let profileRole: UserRole = "customer";
  const services = getFirebaseServices();
  if (services) {
    try {
      const profile = await getDoc(doc(services.db, "users", user.uid));
      const storedRole = profile.data()?.role;
      if (["customer", "reseller", "partner"].includes(storedRole)) profileRole = storedRole as UserRole;
    } catch {
      profileRole = "customer";
    }
  }
  const role = isMasterAdmin
    ? "owner"
    : adminRoles.includes(claimRole as UserRole)
      ? claimRole as UserRole
      : profileRole;
  return {
    uid: user.uid,
    email: user.email ?? "",
    displayName: user.displayName ?? user.email?.split("@")[0] ?? "Pengguna",
    role,
    emailVerified: user.emailVerified,
    providerIds: user.providerData.map((provider) => provider.providerId),
    organizationId: typeof token.claims.organizationId === "string" ? token.claims.organizationId : undefined,
  };
}

export function observeSession(onChange: (session: Session | null) => void): () => void {
  const services = getFirebaseServices();
  if (!services) {
    onChange(null);
    return () => undefined;
  }
  return onAuthStateChanged(services.auth, (user) => {
    if (!user) {
      onChange(null);
      return;
    }
    void sessionFromUser(user).then(onChange).catch(() => onChange(null));
  });
}

export async function login(email: string, password: string): Promise<Session> {
  const services = getFirebaseServices();
  if (!services) throw new Error("Firebase development belum dikonfigurasi.");
  const credential = await signInWithEmailAndPassword(services.auth, email, password);
  return sessionFromUser(credential.user);
}

export async function register(displayName: string, email: string, password: string, accountType: "customer" | "reseller" = "customer"): Promise<Session> {
  const services = getFirebaseServices();
  if (!services) throw new Error("Firebase development belum dikonfigurasi.");
  const credential = await createUserWithEmailAndPassword(services.auth, email, password);
  await updateProfile(credential.user, { displayName });
  await setDoc(doc(services.db, "users", credential.user.uid), {
    displayName,
    email,
    role: accountType,
    emailVerified: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await sendEmailVerification(credential.user, actionCodeSettings());
  return sessionFromUser(credential.user);
}

export async function loginWithGoogle(): Promise<Session> {
  const services = getFirebaseServices();
  if (!services) throw new Error("Firebase development belum dikonfigurasi.");
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const credential = await signInWithPopup(services.auth, provider);
  const userRef = doc(services.db, "users", credential.user.uid);
  const userSnapshot = await getDoc(userRef);
  if (!userSnapshot.exists()) {
    await setDoc(userRef, {
      displayName: credential.user.displayName ?? credential.user.email?.split("@")[0] ?? "Pengguna",
      email: credential.user.email ?? "",
      role: "customer",
      emailVerified: credential.user.emailVerified,
      providers: credential.user.providerData.map((item) => item.providerId),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  return sessionFromUser(credential.user);
}

export async function requestPasswordReset(email: string): Promise<void> {
  const services = getFirebaseServices();
  if (!services) throw new Error("Firebase development belum dikonfigurasi.");
  try {
    await sendPasswordResetEmail(services.auth, email, actionCodeSettings());
  } catch (error) {
    if (error instanceof Error && "code" in error && error.code === "auth/user-not-found") return;
    throw error;
  }
}

export async function resendVerificationEmail(): Promise<void> {
  const services = getFirebaseServices();
  const user = services?.auth.currentUser;
  if (!user) throw new Error("Login terlebih dahulu untuk mengirim ulang verifikasi.");
  if (user.emailVerified) return;
  await sendEmailVerification(user, actionCodeSettings());
}

export async function refreshVerificationStatus(): Promise<Session> {
  const services = getFirebaseServices();
  const user = services?.auth.currentUser;
  if (!user) throw new Error("Sesi login tidak ditemukan.");
  await reload(user);
  return sessionFromUser(user, true);
}

export async function logout(): Promise<void> {
  const services = getFirebaseServices();
  if (services) await signOut(services.auth);
}

export async function activateInitialOwner(): Promise<Session> {
  const services = getFirebaseServices();
  const user = services?.auth.currentUser;
  if (!services || !user) throw new Error("Login terlebih dahulu dengan akun owner.");
  const callable = httpsCallable<void, { uid: string; role: string }>(services.functions, "bootstrapOwner");
  await callable();
  return sessionFromUser(user, true);
}
